import { useState } from 'react';
import {
  Button,
  Chip,
  ConfirmButton,
  FilterBar,
  ListItem,
  OCRConfidence,
  Pill,
  Screen,
  ScreenHeader,
  StateBox,
  Workspace,
  WorkspacePane,
  type PillTone,
} from '@garnet/ui';
import type { InboxDoc, InboxField, VerticalData } from '../data/types.js';

/* Garnet · Document inbox (OCR front door) */

type DocStatus = InboxDoc['status'];

const STATUS: Record<DocStatus, { tone: PillTone; label: string }> = {
  ready: { tone: 'm', label: 'ready' },
  'needs-confirm': { tone: 'a', label: 'needs confirm' },
  translate: { tone: 'a', label: 'translation required' },
};

function StatusPill({ s }: { s: DocStatus }) {
  const m = STATUS[s] ?? STATUS.ready;
  return <Pill tone={m.tone}>{m.label}</Pill>;
}

function FieldRow({ docId, f, confirmed, onConfirm }: {
  docId: string; f: InboxField; confirmed: Set<string>; onConfirm: (key: string) => void;
}) {
  const low = f.low || f.conf < 80;
  const key = docId + ':' + f.k;
  const isDone = confirmed.has(key);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr auto auto', gap: 14, alignItems: 'center',
      padding: '11px 0', borderBottom: '1px solid var(--rule)' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>{f.k}</span>
      <span style={{ fontFamily: f.code ? 'var(--mono)' : 'var(--body)', fontSize: f.code ? 13.5 : 14, fontWeight: f.code ? 600 : 400,
        color: f.v === '—' ? 'var(--muted)' : 'var(--ink)' }}>
        {f.v}{f.expired && <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--flag-ink)', marginLeft: 8 }}>date in past</span>}
      </span>
      <OCRConfidence conf={f.conf} />
      <span style={{ minWidth: 96, display: 'flex', justifyContent: 'flex-end' }}>
        {low ? <ConfirmButton done={isDone} onClick={() => onConfirm(key)} />
          : <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)' }}>claim · verify</span>}
      </span>
    </div>
  );
}

function DocDetail({ doc, confirmed, onConfirm, routed, onRoute }: {
  doc: InboxDoc; confirmed: Set<string>; onConfirm: (key: string) => void;
  routed: Set<string>; onRoute: (id: string) => void;
}) {
  const lowKeys = doc.fields.filter((f) => f.low || f.conf < 80).map((f) => doc.id + ':' + f.k);
  const allConfirmed = lowKeys.every((k) => confirmed.has(k));
  const isTranslate = doc.status === 'translate';
  const isRouted = routed.has(doc.id);
  return (
    <div style={{ padding: '22px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{doc.id} · {doc.pages} pp · {doc.received}</div>
          <h2 className="display" style={{ fontSize: '1.4rem', fontWeight: 600, margin: '5px 0 4px', letterSpacing: '-.01em' }}>{doc.kind}</h2>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{doc.supplier}</div>
        </div>
        <StatusPill s={doc.status} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '18px 0 4px', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Extracted fields</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>· no field is trusted until confirmed</span>
      </div>
      <div style={{ borderTop: '1px solid var(--ink)' }}>
        {doc.fields.map((f) => <FieldRow key={f.k} docId={doc.id} f={f} confirmed={confirmed} onConfirm={onConfirm} />)}
      </div>

      {doc.note && (
        <div className="weak" style={{ marginTop: 16, borderLeftColor: 'var(--attest)', background: 'var(--attest-bg)' }}>
          <span className="wl">rule</span><span className="wt">{doc.note}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {isTranslate ? (
          <>
            <Button variant={isRouted ? 'secondary' : 'primary'} icon={isRouted ? 'check' : 'link'}
              onClick={() => onRoute(doc.id)} disabled={isRouted}>
              {isRouted ? 'Routed to translator' : 'Route to translator'}
            </Button>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
              {isRouted ? 'document checks blocked until certified translation returns' : 'ar-JO → en · blocks dependent document checks until delivered'}
            </span>
          </>
        ) : (
          <>
            <Button variant="primary" icon="check" disabled={!allConfirmed}>Confirm &amp; open as claim</Button>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: allConfirmed ? 'var(--verified-ink)' : 'var(--muted)' }}>
              {allConfirmed ? 'all low-confidence fields confirmed · ready for intake'
                : (lowKeys.length - [...confirmed].filter((k) => lowKeys.includes(k)).length) + ' field(s) below threshold await confirmation'}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

const FILTERS: Array<{ v: 'all' | DocStatus; label: string }> = [
  { v: 'all', label: 'all' }, { v: 'needs-confirm', label: 'needs confirm' },
  { v: 'translate', label: 'translate' }, { v: 'ready', label: 'ready' },
];

export function DocumentInbox({ data }: { data: VerticalData }) {
  const [sel, setSel] = useState<string>(data.inbox[0]?.id ?? '');
  const [filter, setFilter] = useState<'all' | DocStatus>('all');
  const [confirmed, setConfirmed] = useState<Set<string>>(() => new Set());
  const [routed, setRouted] = useState<Set<string>>(() => new Set());

  const list = data.inbox.filter((d) => filter === 'all' ? true : d.status === filter);
  const doc = data.inbox.find((d) => d.id === sel) ?? list[0];

  const confirm = (key: string) => setConfirmed((s) => new Set(s).add(key));
  const route = (id: string) => setRouted((s) => new Set(s).add(id));

  return (
    <Screen workspace>
      <ScreenHeader
        eyebrow="§ Document inbox"
        title="The OCR front door"
        lead="Documents arrive here first. Every extracted field is a claim with a confidence, not a fact — a field below threshold asks for a human before any check trusts it."
      />

      <FilterBar label="show">
        {FILTERS.map((f) => (
          <Chip key={f.v} active={filter === f.v} onClick={() => setFilter(f.v)}>
            {f.label}
          </Chip>
        ))}
      </FilterBar>

      <Workspace rail={320}>
        <WorkspacePane>
          <div className="pane-sticky" style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', padding: '12px 14px' }}>
            Queue · {list.length}
          </div>
          {list.length === 0 ? (
            <div style={{ padding: 20 }}><StateBox kind="empty" title="Nothing in this filter" /></div>
          ) : (
            <div className="list">
              {list.map((d) => (
                <ListItem key={d.id} active={d.id === doc?.id} onSelect={() => setSel(d.id)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{d.id}</span>
                    <StatusPill s={d.status} />
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 500, marginTop: 5 }}>{d.kind}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{d.supplier}</div>
                </ListItem>
              ))}
            </div>
          )}
        </WorkspacePane>
        {doc ? (
          <WorkspacePane>
            <DocDetail doc={doc} confirmed={confirmed} onConfirm={confirm} routed={routed} onRoute={route} />
          </WorkspacePane>
        ) : (
          <WorkspacePane flush>
            <StateBox kind="empty" title="Select a document" />
          </WorkspacePane>
        )}
      </Workspace>
    </Screen>
  );
}
